import { WELCOME_PAGE_ENABLED } from '../base/flags/constants';
import { getFeatureFlag } from '../base/flags/functions';
import { toState } from '../base/redux/functions';
import { MOBILE_USERNAME, MOBILE_TOKEN, MOBILE_OWNER, MOBILE_MODERATOR_NAME, MOBILE_MODERATOR_AVATAR, MOBILE_MODERATOR_ACTIVATIONDATE, MOBILE_MODERATOR_ROOMNAME } from '../base/flags/constants';
/**
 * Determines whether the {@code WelcomePage} is enabled.
 *
 * @param {IStateful} stateful - The redux state or {@link getState}
 * function.
 * @returns {boolean} If the {@code WelcomePage} is enabled by the app, then
 * {@code true}; otherwise, {@code false}.
 */
export function isWelcomePageEnabled(stateful) {
    if (navigator.product === 'ReactNative') {
        return getFeatureFlag(stateful, WELCOME_PAGE_ENABLED, false);
    }
    const config = toState(stateful)['features/base/config'];
    return !config.welcomePage?.disabled;
}
/**
 * Returns the configured custom URL (if any) to redirect to instead of the normal landing page.
 *
 * @param {IStateful} stateful - The redux state or {@link getState}.
 * @returns {string} - The custom URL.
 */
export function getCustomLandingPageURL(stateful) {
    return toState(stateful)['features/base/config'].welcomePage?.customUrl;
}
/**
 * Get the WS URL
 */
export function getWSUrl() {
    return location.hostname;
}
/**
 * Get the prosody username for Mobile App
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobileUsername(stateful) {
    return getFeatureFlag(stateful, MOBILE_USERNAME, '');
}
/**
 * Get the prosody token for Mobile App
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobileToken(stateful) {
    return getFeatureFlag(stateful, MOBILE_TOKEN, '');
}
/**
 * Check if the user is the owner of the room in Mobile App
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function isMobileOwner(stateful) {
    return Boolean(getFeatureFlag(stateful, MOBILE_OWNER));
}
/**
 * Get the name of the room moderator
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobileModeratorName(stateful) {
    return getFeatureFlag(stateful, MOBILE_MODERATOR_NAME, '');
}
/**
 * Get the avatar of the room moderator
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobileModeratorAvatar(stateful) {
    return getFeatureFlag(stateful, MOBILE_MODERATOR_AVATAR, '');
}
/**
 * Get the avatar of the room moderator
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobileModeratorActivationDate(stateful) {
    return getFeatureFlag(stateful, MOBILE_MODERATOR_ACTIVATIONDATE, '');
}
/**
 * Get the roomName of moderator
 *  @param {Function|Object} stateful - The redux state or {@link getState}
 */
export function getMobilePersonalRoomName(stateful) {
    return getFeatureFlag(stateful, MOBILE_MODERATOR_ROOMNAME, '');
}
/*
* Start the CAS (OIDC) login: Server_Node redirects to CAS and, after the callback,
* back to the welcome page (or to "#loginError=<result>" if loginMeetSSO fails).
* prompt=login: CAS always asks for the credentials, even if the user has a CAS session,
* so that after a refused login the user can log in with another account.
*/
export function startSSOLogin() {
    window.location.href = window.location.origin + "/login?prompt=login";
}
/*
* Get the loginMeetSSO result code from "#loginError=<result>", or '' if absent
*/
export function getLoginErrorCode() {
    const match = window.location.hash.match(/loginError=(-?\d+)/);
    return match ? match[1] : '';
}
/*
* Get the user of the CAS login just completed, handed over once by Server_Node (GET /session),
* and save it in localStorage "user" as the password login did.
* Returns the user, or null if no CAS login was just completed (localStorage is left untouched)
*/
export async function fetchSSOLogin() {
    try {
        const res = await fetch(window.location.origin + "/session", {
            credentials: "same-origin",
            cache: "no-store"
        });
        if (!res.ok) {
            return null;
        }
        const user = await res.json();
        window.localStorage.setItem("user", JSON.stringify(user));
        return user;
    }
    catch (error) {
        return null;
    }
}
/*
* Delete user local storage and redirect to welcome page
*/
export function logoutVianovaUser() {
    window.localStorage.removeItem("user");
    window.localStorage.removeItem("features/base/settings");
    window.localStorage.removeItem("jitsiMeetId");
    //window.localStorage.removeItem("features/recent-list");  //Riattivata la cancellazione al logout per cancellare le vecchie stanze senza brand
    window.localStorage.removeItem("showAgainNews"); //cancello la vecchia etichetta per le novità di Marzo 2021
    window.localStorage.removeItem("showAgainNewsMay2021"); //cancello la vecchia etichetta per le novità di Maggio 2021
    window.localStorage.removeItem("showAgainNewsApril2022"); //cancello la vecchia etichetta per le novità di Aprile 2022
    window.localStorage.removeItem("showAgainNewsOctober2022"); //cancello la vecchia etichetta per le novità di Ottobre 2022 (etichetta usata solo in preprod)
    window.localStorage.removeItem("showAgainNewsDecember2022"); //cancello la vecchia etichetta per le novità di Dicembre 2022
    window.localStorage.removeItem("showAgainNewsFebruary2023"); //cancello la vecchia etichetta per le novità di Febbraio 2023 (etichetta usata solo in preprod)
    window.localStorage.removeItem("showAgainNewsMarch2023"); //cancello la vecchia etichetta per le novità di Marzo 2023
    window.localStorage.removeItem("showAgainNewsJuly2023"); //cancello la vecchia etichetta per le novità di Luglio 2023
    window.localStorage.removeItem("showAgainNewsDecember2024"); //cancello la vecchia etichetta per le novità di Dicembre 2024
    window.localStorage.removeItem("showAgainNewsDecember2025"); //cancello la vecchia etichetta per le novità di Dicembre 2025
    window.localStorage.removeItem("showAgainNewsMarch2026"); //cancello la vecchia etichetta per le novità di Marzo 2026
    window.localStorage.removeItem("showAgainNewsJune2026"); //cancello la vecchia etichetta per le novità di Giugno 2026
    window.localStorage.removeItem("showAgainNewsJuly2026"); //cancello la vecchia etichetta per le novità di Luglio 2026 (etichetta usata solo in preprod)
    //window.localStorage.removeItem("showAgainNewsAugust2026");  //cancello la vecchia etichetta per le novità di Luglio 2026
    window.location.href = window.location.origin;
}
